/**
 * ConsentPanel — waar een ouder toestemming geeft of intrekt.
 *
 * De tekst hier is geen bijzaak. Toestemming onder de AVG moet GEÏNFORMEERD
 * zijn: de ouder moet weten waarvoor hij tekent, in gewone taal, vóórdat hij
 * klikt. Daarom staat er expliciet wat er wél en niet wordt bewaard, en dat
 * intrekken altijd kan.
 *
 * TWEE OUDERS. Toestemming is een eigenschap van het KIND, niet van één ouder.
 * Zijn er twee gekoppeld, dan geldt één gedeelde toestemming. Dat moet
 * zichtbaar zijn, anders:
 *   - denkt ouder B dat hij nog iets moet doen terwijl het al geregeld is, of
 *   - trekt ouder B "zijn" toestemming in en wist daarmee ongemerkt de data
 *     waar ouder A voor tekende.
 *
 * Daarom tonen we bij toestemming van de ándere ouder een gedempte staat met
 * naam en datum, en zit intrekken achter een tweede, zwaardere stap. We
 * blokkeren het niet: juridisch mag elke gezagdrager intrekken. Maar het mag
 * geen ongeluk zijn.
 */

import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { fetchLinkedPeople, getActiveProfile, type LinkedPerson } from '../../../data/supabase/session';
import { grantConsent, withdrawConsent } from '../../../data/supabase/consent';
import { deleteAssessmentDataForLearner } from '../../../assessment/utils/assessmentStorage';
import { error as logError } from '../../../utils/logger';
function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
export function ConsentPanel() {
  const [children, setChildren] = useState<LinkedPerson[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState<LinkedPerson | null>(null);
  const myId = getActiveProfile()?.id ?? null;
  const load = () => {
    void fetchLinkedPeople().then(people => setChildren(people.filter(p => p.relation === 'child')));
  };
  useEffect(load, []);
  const handleGrant = async (child: LinkedPerson) => {
    setBusyId(child.id);
    setNotice(null);
    const res = await grantConsent(child.id);
    setBusyId(null);
    setNotice(res.ok ? `Toestemming gegeven voor ${child.name}. Toetsresultaten worden vanaf nu bewaard.` : res.message ?? 'Er ging iets mis.');
    if (res.ok) load();
  };
  const handleWithdraw = async (child: LinkedPerson) => {
    setConfirmWithdraw(null);
    setBusyId(child.id);
    setNotice(null);
    const res = await withdrawConsent(child.id);
    if (res.ok) {
      // De RPC wist de SERVERKOPIE. Dat was tot nu toe het hele verhaal, en
      // daarmee klopte de melding niet: de toetsuitslagen van dit kind staan
      // óók op dit apparaat, en die bleven staan. Op een gedeeld gezinstablet
      // — waar de ouder net introk en het kind straks weer oefent — bleven ze
      // gewoon leesbaar.
      //
      // De opruiming in `App.tsx` dekt dat niet: die vuurt pas als het KIND
      // zelf op dit apparaat inlogt. Trekt een ouder in op zijn eigen
      // telefoon, dan gebeurt daar nooit iets.
      //
      // Bewust leerling-gescoped en niet `clearAllAssessmentData()`: anders
      // verliest een broertje of zusje op hetzelfde apparaat zijn gegevens
      // door een beslissing die niet over hem ging.
      await deleteAssessmentDataForLearner(child.id).catch(err => logError('[Consent] Lokale toetsdata wissen mislukt', err));
      setBusyId(null);
      const n = (res.deleted?.reports ?? 0) + (res.deleted?.attempts ?? 0);
      // Benoem wat er gewist is. "Ingetrokken" zonder gevolg klinkt als een
      // vinkje; de ouder moet weten dat er daadwerkelijk data is verwijderd.
      setNotice(n > 0 ? `Toestemming ingetrokken. ${n} bewaarde toetsgegevens zijn verwijderd. ${child.name} kan gewoon blijven oefenen.` : `Toestemming ingetrokken. De bewaarde toetsgegevens zijn verwijderd. ${child.name} kan gewoon blijven oefenen.`);
      load();
    } else {
      setNotice(res.message ?? 'Er ging iets mis.');
    }
  };
  return <section className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm" aria-labelledby="consent-heading" data-id="element-8417" data-mp="2848t14an7d7s">
      
      <h3 id="consent-heading" className="font-black text-slate-900 text-lg flex items-center gap-3 mb-2" data-id="element-8418" data-mp="1bgufbewdhudg">
        
        <ShieldCheck className="w-5 h-5 text-indigo-500" data-id="element-8419" data-mp="1gbzw5zax4iuo" />
        Toestemming voor het bewaren van gegevens
      </h3>
      <p className="text-sm text-slate-600 mb-5" data-id="element-8420" data-mp="1p6efccykkdj2">
        Oefenen werkt altijd — daar is geen toestemming voor nodig. Voor het{' '}
        <strong data-id="element-8421" data-mp="sngsjx2tcu8x">bewaren van toetsresultaten en rapporten</strong> vraagt de wet
        toestemming van een ouder, omdat dit een beoordeling van uw kind is. U
        kunt dit op elk moment intrekken; bewaarde resultaten worden dan
        verwijderd.
      </p>

      {notice && <div role="status" className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-8422" data-mp="k7r9uve4cued">
        
          {notice}
        </div>}

      {children === null ? <p className="text-sm text-slate-400" data-id="element-8423" data-mp="g7qriznwfz21">Laden…</p> : children.length === 0 ? <p className="text-sm text-slate-600" data-id="element-8424" data-mp="b6jnnbovnk0n">
          Er is nog geen kind aan uw account gekoppeld. Vraag uw kind om de
          koppelcode uit zijn instellingen.
        </p> : <ul className="space-y-3" data-id="element-8425" data-mp="1fynhjtk27fm">
          {children.map(child => {
        const granted = child.consentStatus === 'granted';
        const byMe = granted && child.consentGrantedBy === myId;
        const byOther = granted && !byMe;
        const otherName = child.consentGrantedByName ?? 'de andere ouder';
        const date = formatDate(child.consentGrantedAt);
        return <li key={child.id} className={`p-4 rounded-xl border ${byOther ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`} data-id="element-8426" data-mp="3en0609mw01zt">
              
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-8427" data-mp="34ymiao2hq9ea">
                  <div className="flex items-center gap-3" data-id="element-8428" data-mp="3a0bwgcl0f36l">
                    {granted ? <ShieldCheck className={`w-5 h-5 flex-shrink-0 ${byOther ? 'text-slate-400' : 'text-emerald-500'}`} data-id="element-8429" data-mp="2v2t4z2gy6bvg" /> : <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0" data-id="element-8430" data-mp="301maa7m2ywkj" />}
                    <div data-id="element-8431" data-mp="3nq5low0ffpgu">
                      <div className="font-bold text-slate-900" data-id="element-8432" data-mp="3nqxfc1agrd4h">
                        {child.name}
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-8433" data-mp="3srcu8xae8sok">
                        {granted ? 'Toetsresultaten worden bewaard.' : 'Alleen oefenvoortgang. Toetsresultaten blijven op het apparaat van uw kind.'}
                      </div>
                    </div>
                  </div>

                  {/* Gaf de ándere ouder toestemming, dan is er voor deze ouder
                     niets te doen. Een actieve knop zou suggereren dat er nog
                     iets moet gebeuren. */}
                  {byOther ? <span className="text-sm font-semibold text-slate-400 flex-shrink-0" data-id="element-8434" data-mp="1v1xski8vokwj">
                      Al geregeld
                    </span> : <Button variant={granted ? 'secondary' : 'primary'} disabled={busyId === child.id} onClick={() => granted ? setConfirmWithdraw(child) : void handleGrant(child)} className="w-full sm:w-auto flex-shrink-0" data-id="element-8435" data-mp="29r3u4cv6un7l">
                  
                      {busyId === child.id ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-8436" data-mp="1c323flbvmwpk" /> : granted ? 'Intrekken' : 'Toestemming geven'}
                    </Button>}
                </div>

                {/* Herkomst van de toestemming. Bij twee ouders is dit het
                   verschil tussen begrijpen en gissen. */}
                {byOther && <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-600" data-id="element-8437" data-mp="31hqftmkgwenl">
                    <p className="flex items-start gap-2" data-id="element-8438" data-mp="2xgokvhxl2orf">
                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" data-id="element-8439" data-mp="2xg562a38cghm" />
                      <span data-id="element-8440" data-mp="uvax5ly7pb25">
                        <strong data-id="element-8441" data-mp="urwsg722sprj">{otherName}</strong> gaf op {date} toestemming
                        voor {child.name}. Er is één toestemming per kind, dus u
                        hoeft niets te doen — u ziet de resultaten gewoon.
                      </span>
                    </p>
                    <button type="button" onClick={() => setConfirmWithdraw(child)} className="mt-2 ml-6 text-sm font-semibold text-rose-600 hover:text-rose-700 underline underline-offset-2" data-id="element-8442" data-mp="19kqiunivyqaz">
                  
                      Toch intrekken
                    </button>
                    <p className="mt-1 ml-6 text-xs text-slate-500" data-id="element-8443" data-mp="1niybij8w35gc">
                      Dit geldt voor het hele account en verwijdert ook de
                      resultaten die {otherName} bewaart. Overleg dit eerst.
                    </p>
                  </div>}

                {byMe && date && <p className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-500" data-id="element-8444" data-mp="3u9wdfd6gvbmh">
                    U gaf toestemming op {date}.
                    {child.parentCount > 1 && ` Er is nog een ouder gekoppeld; intrekken geldt ook voor hem of haar.`}
                  </p>}
              </li>;
      })}
        </ul>}

      <ConfirmDialog open={confirmWithdraw !== null} title="Toestemming intrekken" message={confirmWithdraw ? confirmWithdraw.consentGrantedBy && confirmWithdraw.consentGrantedBy !== myId ? `${confirmWithdraw.consentGrantedByName ?? 'De andere ouder'} gaf deze toestemming. Als u intrekt, worden alle bewaarde toetsresultaten en rapporten van ${confirmWithdraw.name} verwijderd — ook voor de andere ouder. Dit kan niet ongedaan worden gemaakt. ${confirmWithdraw.name} kan daarna gewoon blijven oefenen.` : `Alle bewaarde toetsresultaten en rapporten van ${confirmWithdraw.name} worden verwijderd. Dit kan niet ongedaan worden gemaakt. ${confirmWithdraw.name} kan daarna gewoon blijven oefenen.` : ''} confirmLabel="Intrekken en verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => confirmWithdraw && void handleWithdraw(confirmWithdraw)} onCancel={() => setConfirmWithdraw(null)} data-id="element-8445" data-mp="22lybd7hejo9v" />
      
    </section>;
}